import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AlertTriangle, MailCheck, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * VerificationBanner (Polish-Package, Soft-Enforcement)
 *
 * Zeigt eingeloggten Kunden mit ``email_verified === false`` einen
 * dezenten, dismissbaren Hinweisbanner — verhindert aber NICHT das
 * Nutzen des Portals. Die „Erneut senden"-Aktion ruft den bestehenden
 * ``/customer-auth/resend-verification``-Endpoint.
 *
 * Wird pro Session ausgeblendet, wenn der Nutzer auf X klickt. Beim
 * nächsten Login ist er wieder da (bewusst: der Nutzer soll die
 * Verifikation nicht dauerhaft ignorieren).
 */
export default function VerificationBanner() {
  const [dismissed, setDismissed] = useState(false);
  const me = useQuery({
    queryKey: ["portal-me-verification"],
    queryFn: async () => (await api.get("/customer/me")).data,
    staleTime: 60_000,
  });
  const resend = useMutation({
    mutationFn: async () => {
      const email = me.data?.email;
      if (!email) throw new Error("Keine E-Mail-Adresse gefunden");
      return (await api.post("/customer-auth/resend-verification", { email })).data;
    },
    onSuccess: () => toast.success(
      "Falls dein Konto noch nicht verifiziert ist, wurde die Bestätigung erneut versendet."
    ),
    onError: () => toast.error("Verifizierungsmail konnte nicht angefordert werden."),
  });

  if (dismissed) return null;
  if (!me.data || me.data.email_verified) return null;

  return (
    <div
      data-testid="portal-verification-banner"
      className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-100 px-4 py-3 flex flex-wrap items-center gap-3"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <AlertTriangle size={16} className="shrink-0 text-amber-400" />
        <div className="text-sm min-w-0">
          <div className="font-semibold">E-Mail-Adresse noch nicht bestätigt</div>
          <div className="text-xs text-amber-200/80 truncate">
            Bitte prüfe dein Postfach ({me.data.email}) und klicke den Link. Volle Nutzung ohne Einschränkungen möglich.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          data-testid="portal-verification-resend"
          onClick={() => resend.mutate()}
          disabled={resend.isPending}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-amber-400/60 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs uppercase tracking-widest font-semibold"
        >
          <MailCheck size={13} /> Erneut senden
        </button>
        <Link
          to="/portal/account"
          className="text-xs uppercase tracking-widest text-amber-300 hover:text-amber-100"
        >
          Konto
        </Link>
        <button
          type="button"
          aria-label="Hinweis ausblenden"
          data-testid="portal-verification-dismiss"
          onClick={() => setDismissed(true)}
          className="text-amber-300/70 hover:text-amber-100 p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
